import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http'
import { inject } from '@angular/core'

import { catchError, throwError } from 'rxjs'

import { AuthService } from '@yoshu-lib/core/auth/auth.service'
import { ToastService } from '@yoshu-lib/core/toast/toast.service'

import { TranslateService } from '@ngx-translate/core'

import { environment } from '@coty/env/environment'

import { ApiError } from './error.model'

/**
 * An HTTP interceptor function that handles errors in HTTP requests.
 *
 * This interceptor catches HTTP errors and performs specific actions based on the error status.
 * For example, if the error status is 401 (Unauthorized), it redirects the user to the login page.
 * Additionally, it logs the error to the console and rethrows it for further handling.
 *
 * @param req - The outgoing HTTP request.
 * @param next - The next interceptor or backend handler in the chain.
 * @returns An observable of the HTTP event stream, with error handling applied.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService)
  const toast = inject(ToastService)
  const translate = inject(TranslateService)

  const isYoshuApiRequest = req.url.startsWith(environment.apiUrl)

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (isYoshuApiRequest) {
        const yoshuError = error.error as ApiError

        if (typeof yoshuError.detail === 'string') {
          toast.error(yoshuError.detail)
        } else if (req.url.includes('/v1/login')) {
          authService.logout().subscribe()
          toast.error(error.message)
        } else {
          const unknownErrorMessage = translate.instant('errors.unknown_error') as string
          toast.error(unknownErrorMessage)
        }
      }

      if (error.status === 401) {
        authService.logout().subscribe()
      }

      console.error('HTTP error: ', error)

      return throwError(() => error)
    })
  )
}
